import { Injectable, inject } from '@angular/core';
import { WebSocketService } from '../../../services/web-socket.service';
import { BehaviorSubject, merge, Observable, of } from 'rxjs';
import { filter, first, map, mergeMap, switchMap, take, tap } from 'rxjs/operators';
import { ScrollService } from '../../../services/scroll.service';
import { TranslationService } from '../../../services/translation/translation.service';
import { GamesService } from '../../../services/games/games.service';
import { FiltersService } from '../../../services/filters.service';
import { TournamentsService } from '../../../../page/tournaments/tournaments.service';
import { LanguageService } from '../../../services/language.service';
import { LootboxService } from '../../../services/lootbox/lootbox.service';
import { DecimalPipe } from '@angular/common';
import { LocalstorageService } from '../../../services/localstorage.service';
import { ArrayService } from '../../../services/array.service';
import { PlatformService } from 'src/app/core/services/platform.service';
import { InstallAppService } from '../../../services/install-app.service';
import { CookieService } from 'ngx-unificator/services';
import { UserService } from '../../../services/user/user.service';
import { CustomNotificationCenterService } from './custom-notification-center.service';
import {
  BonusesWSType,
  FreespinsWSType, GameLimitWSType, LootboxesWSType, NotificationType, PaymentsWSType,
  TournamentsStartWSType,
  TournamentsStatusItemWS,
} from '../../../types/notifications-type';
import {
  CustomNotificationChanelEnum,
  GameLimitsReasonEnum,
  GameLimitsReasonType,
} from '../../../enums/notifications-enum';
import { getRandomNumber } from 'ngx-unificator/helpers';
import {CurrencyService} from "../../../services/currency.service";

export const CUSTOM_LOOTBOX_BY_TITLE = {
  lootbox_spinder: {
    link: '/promo/valentine',
    key: 't.notify-spinder-lootbox-desc',
  },
};

@Injectable({
  providedIn: 'root',
})
export class NotificationCenterService {
  private _ws = inject(WebSocketService);
  private _scroll = inject(ScrollService);
  private _translate = inject(TranslationService);
  private _games = inject(GamesService);
  private _filters = inject(FiltersService);
  private _tournaments = inject(TournamentsService);
  private _lang = inject(LanguageService);
  private _lootbox = inject(LootboxService);
  private _number = inject(DecimalPipe);
  private _localStorage = inject(LocalstorageService);
  private _array = inject(ArrayService);
  private _platform = inject(PlatformService);
  private _installApp = inject(InstallAppService);
  private _cookie = inject(CookieService);
  private _user = inject(UserService);
  private _customNotify = inject(CustomNotificationCenterService);
  private _currency = inject(CurrencyService);

  /**
   * Is sidenav menu opened
   */
  public sideNavOpen: boolean;

  public isNotUnique: boolean;

  public isOpened = false;

  /**
   * Notifications subject
   */
  public notifications$: BehaviorSubject<NotificationType[]> = new BehaviorSubject<any>([]);

  private _bonuses$: Observable<NotificationType> = this._ws.bonuses$.pipe(
    map(e => this._resolveNotifyBonuses(e)),
  );

  private _freespins$: Observable<NotificationType> = this._ws.freespins$.pipe(
    map(e => this._resolveNotifyFreespins(e)),
  );

  private _tournaments$: Observable<NotificationType> = this._ws.tournaments$.pipe(
    switchMap(e => this._resolveTournaments(e)),
  );

  private _tournamentsStarted$: Observable<NotificationType> = this._ws.tournamentsStarted$.pipe(
    switchMap(e => this._resolveTournamentsStarted(e)),
  );

  private _lootboxes$: Observable<NotificationType> = this._ws.lootboxes$.pipe(
    switchMap(e => this._resolveNotifyLootboxes(e)),
  );

  private _payments$: Observable<NotificationType> = this._ws.payments$.pipe(
    map(e => this._resolveNotifyPayments(e)),
  );

  private _gameLimits$: Observable<NotificationType> = this._ws.limits$.pipe(
    switchMap(e => this._resolveGameLimits(e)),
  );

  /**
   * Init notifications
   */
  public initNotify() {
    if (this._platform.isBrowser) {
      this._localStorage.clearItem('notifications');
      merge(
        this._freespins$, this._bonuses$, this._lootboxes$, this._tournamentsStarted$,
        this._payments$, this._gameLimits$, this._tournaments$,
        ...this._customNotify.notificationSubjectList$,
      ).pipe(
        filter(notification => !!notification),
        mergeMap(notification => notification instanceof Observable ? notification : of(notification)),
        map(notification => Array.isArray(notification) ? notification : [notification]),
        tap((notificationList) => {
          const current = this.notifications$.getValue();

          const newList = current.filter(n =>
            !notificationList.some(newItem => newItem.notify_channel === n.notify_channel)
          );

          this.notifications$.next([...notificationList, ...newList]);
        }),
      ).subscribe();
    }
  }

  /**
   * Open sidenav menu
   */
  public open() {
    if (!this.sideNavOpen) {
      this.sideNavOpen = true;
      this._markNotifyRead();
      this.isOpened = true;
      this._scroll.blockScroll();
    }
  }

  /**
   * Close sidenav menu
   */
  public close() {
    if (this.sideNavOpen) {
      this.sideNavOpen = false;
      this._scroll.UnblockScroll();
      this._markNotifyRead();
    }
  }

  /**
   * Toggle sidenav menu
   */
  public toggle() {
    this.sideNavOpen ? this.close() : this.open();
  }

  /**
   * Remove notifications
   * @param notify
   */
  public removeNotify(notify: NotificationType) {
    const current = this.notifications$.getValue();
    const filtered = current.filter(n => n.uid !== notify.uid);

    this.notifications$.next(filtered);

    if (!filtered.length) {
      this.toggle();
    }

    const channel = notify?.notify_channel;
    const domain = window.location.hostname as any;
    const path = '/';

    switch (channel) {
      case CustomNotificationChanelEnum.INSTALL_APP:
        this._installApp.closeNotificationBlock();
        break;

      case CustomNotificationChanelEnum.RELOAD_BONUSES: {
        const now = new Date();
        const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
        this._cookie.set('reload-bonus', '1', endOfDay, path, domain);
        break;
      }

      case CustomNotificationChanelEnum.WELCOME_BONUSES: {
        const key = `welcome-bonus-${this._user.stats.deposits_count}`;
        this._cookie.set(key, '1', 999, path, domain);
        break;
      }

      case CustomNotificationChanelEnum.BLOG_NOTIFICATIONS:
        if (notify.slug) {
          this._cookie.set(notify.slug, '1');
        }
        break;
    }
  }

  /**
   * Map game and get game name
   * @param notification
   * @private
   */
  private _mapGameFromSocket(notification: GameLimitWSType): Observable<string> {
    return this._games.list({
      'external_id[]': notification.game,
    }).pipe(
      first(),
      filter(response => !!response),
      map(gameResponse => {
        const gamesByExternalId = this._filters.valueAsKeyObject('externalId', gameResponse.gameList || []);
        return gamesByExternalId[notification.game]?.name;
      }),
    );
  }

  /**
   * Map reason of game limit
   * @param notification
   * @param gameName
   * @private
   */
  private _mapReasonOfLimit(notification: GameLimitWSType, gameName: string) {
    return {
      uid: getRandomNumber(111, 111111111111111),
      link: '/',
      title: this._translate.translate(this._getTranslateForGameReason(notification.reason), {
        game: gameName,
        limit: notification.limit_cents ?
          this._currency.subunitsToUnits(notification.limit_cents, notification.currency) : '',
        currency: notification.currency,
      }),
      img: '/assets/svg/notify/limits.svg',
    };
  }

  private _resolveTournamentsStarted(notification: TournamentsStartWSType): Observable<NotificationType> {
    return this._tournaments.list({ with_game_list: 1 }).pipe(
      filter(list => !!list),
      map(list => [...list.now]),
      map((list) => {
        const currentTournament = list.find(tournament => Number(tournament.identifier) === notification?.id);
        return {
          uid: notification.id,
          title: this._translate.translate('t.tournament-started', {
            title: currentTournament?.Title,
          }),
          link: `/${this._lang.currentRouteLanguage}/tournaments/${currentTournament.slug}`,
          img: '/assets/svg/notify/tournaments.svg',
        };
      }),
    );
  }

  private _resolveTournaments(notification: TournamentsStatusItemWS): Observable<NotificationType> {
    return this._tournaments.list({ with_game_list: 1 }).pipe(
      filter(list => !!list),
      map(list => [...list.now, ...list.future, ...list.past]),
      map((cmsTournaments: any) => {
        return Object.values(notification).map((notify) => {
          const currentTournament = cmsTournaments.find(t => Number(t.identifier) === notify?.tournament_id);

          if (currentTournament && Boolean(notify?.award_place)) {
            return {
              uid: notify.tournament_id,
              title: this._translate.translate('t.notify-tournaments-desc', {
                award_place: notify?.award_place,
                points: notify?.points,
                title: currentTournament?.Title,
              }),
              img: '/assets/svg/notify/tournaments.svg',
              link: `/${this._lang.currentRouteLanguage}/tournaments/${currentTournament.slug}`,
            };
          }
        });
      })[0],
    );
  }

  /**
   * Resolve games limits
   * @param notification
   * @private
   */
  private _resolveGameLimits(notification: GameLimitWSType): Observable<NotificationType> {
    return this._mapGameFromSocket(notification).pipe(
      map(gameName => {
        return this._mapReasonOfLimit(notification, gameName);
      }),
    );
  }

  /**
   * Resolve bonuses
   * @param notification
   * @private
   */
  private _resolveNotifyBonuses(notification: BonusesWSType): NotificationType {
    return {
      uid: notification.id,
      link: '/account',
      title: this._translate.translate('t.notify-bonuses-desc', {
        bonus: this._getTranslateForBonus(notification.title),
      }),
      img: '/assets/svg/notify/bonuses.svg',
    };
  }

  /**
   * Resolve freespins
   * @param notification
   * @private
   */
  private _resolveNotifyFreespins(notification: FreespinsWSType): NotificationType {
    return {
      uid: notification.id,
      link: '/account',
      title: this._translate.translate('t.notify-freespins-desc', {
        count: notification.freespins_total,
      }),
      img: '/assets/svg/notify/freespins.svg',
    };
  }

  /**
   * Resolve lootboxes
   * @param notification
   * @private
   */
  private _resolveNotifyLootboxes(notification: LootboxesWSType): Observable<NotificationType> {
    return this._lootbox.lootboxesTranslates$.pipe(
      take(1),
      map(() => {
        const translate = this._lootbox.resolveLootboxTranslates(notification);
        return {
          uid: notification.id,
          link: CUSTOM_LOOTBOX_BY_TITLE[notification.group_key] ?
            `${CUSTOM_LOOTBOX_BY_TITLE[notification.group_key]?.link}` :
            `/${this._lang.currentRouteLanguage}/account`,
          title: this._translate.translate(
            CUSTOM_LOOTBOX_BY_TITLE[notification.group_key]?.key || 't.notify-lootbox-desc',
            CUSTOM_LOOTBOX_BY_TITLE[notification.group_key]?.description ? null :
              { bonus: this._translate.translate(translate.title) },
          ),
          img: '/assets/svg/notify/lootboxes.svg',
        };
      }),
    );
  }

  /**
   * Resolve payment changes
   * @param notification
   * @private
   */
  private _resolveNotifyPayments(notification: PaymentsWSType) {
    return {
      uid: notification.id,
      link: '/account/balance',
      title: this._translate.translate(notification.success ?
        't.notify-payments-desc-accepted' :
        't.notify-payments-desc-discarded', {
        action: notification.action,
        payment_system: notification.payment_system,
        amount_cents: this._number.transform(
          this._currency.subunitsToUnits(notification.amount_cents, notification.currency),
          this._currency.isCryptoCurrency(notification.currency) ? '0.8' : '0.2'),
        currency: notification.currency,
      }),
      img: '/assets/svg/notify/payments.svg',
    };
  }

  /**
   * Get translate key for game reason
   * @param reason
   * @private
   */
  private _getTranslateForGameReason(reason: GameLimitsReasonType) {
    return reason === GameLimitsReasonEnum.FORBIDDEN_WITH_BONUS ? 't.game-forbidden-with-bonus' :
      reason === GameLimitsReasonEnum.BONUS_BET_LIMIT_EXCEEDED ? 't.bonus-bet-limit' :
        reason === GameLimitsReasonEnum.BET_LIMIT_EXCEEDED ? 't.bet-limit' :
          reason === GameLimitsReasonEnum.SESSION_LIMIT_EXCEEDED ? 't.session-limit' : '';
  }

  private _markNotifyRead() {
    this._checkUniqueNotify();
    this._localStorage.set('notifications', JSON.stringify(this.notifications$.getValue()));
  }

  private _checkUniqueNotify() {
    const notifyArr: any = JSON.parse(this._localStorage.get('notifications'));
    if (!notifyArr) {
      return;
    }
    const notifyArrNow = this.notifications$.getValue();

    const result = this._array.arrayDifference(
      notifyArr,
      notifyArrNow,
      (o1, o2) => o1.id === o2.id,
    );

    if (result.length > 0) {
      this.isNotUnique = false;
      this.isOpened = false;
    } else {
      this.isNotUnique = true;
    }
  }

  /**
   * Get translate key for bonus
   * @param bonusTitle
   * @private
   */
  private _getTranslateForBonus(bonusTitle: string) {
    return bonusTitle.replace(/\[\S*]\s/gim, '');
  }

}
